import React from 'react';
import { ProvenanceStrip } from '../report/ProvenanceStrip';
import { EditableHeading } from '../report/EditableHeading';
import { MetaLeakChip } from '../report/MetaLeakChip';
import {
  REPORT_SECTION_H2_CLASS,
  REPORT_SECTION_HEADING_TEXT_CLASS,
  reportSectionHeadingStyleScaled,
} from '../../../lib/reportSectionHeading';
import { resolveSectionEmptyMessage } from '../../../lib/sectionFallbacks/derivedFallbacks';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../lib/reportRoleColors';
import type { SiteRagReport } from '../../../types/siteRag';

/**
 * Section identity context — SectionsRouter sets this for each
 * dispatched section so SectionShell can read the stable `sectionId`
 * WITHOUT modifying the 37 modern section component signatures.
 */
const SectionIdContext = React.createContext<string | undefined>(undefined);

/** Router-level chrome (EditableHeading, meta-leak regen) from SectionsRouter. */
export interface RouterSectionChromeContextValue {
  isEditing?: boolean;
  headingOverrides?: Record<string, string>;
  onHeadingChange?: (key: string, value: string) => void;
  sectionDefaultHeading?: (key: string, fallback: string) => string;
  isAdmin?: boolean;
  onSectionRegen?: (sectionId: string) => Promise<void> | void;
}

const RouterSectionChromeContext = React.createContext<RouterSectionChromeContextValue>({});

/** Used by SectionsRouter to set section identity for wrapped components. */
export function SectionIdProvider({ value, children }: { value: string; children: React.ReactNode }): React.ReactElement {
  return <SectionIdContext.Provider value={value}>{children}</SectionIdContext.Provider>;
}

/** Read the router-assigned sectionId (undefined when mounted standalone outside SectionsRouter). */
export function useSectionIdContext(): string | undefined {
  return React.useContext(SectionIdContext);
}

export const SectionShellContext = React.createContext<{ report?: SiteRagReport; sectionId?: string; isEditorialActive?: boolean }>({});

export function useSectionShellContext() {
  return React.useContext(SectionShellContext);
}

/** Used by SectionsRouter to pass heading-edit + regen affordances. */
export function RouterSectionChromeProvider({
  value,
  children,
}: {
  value: RouterSectionChromeContextValue;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <RouterSectionChromeContext.Provider value={value}>
      {children}
    </RouterSectionChromeContext.Provider>
  );
}

interface SectionShellProps {
  /** Section heading — typically the defaultLabel from getSectionsForType. */
  label: string;
  /** True if the section's source data is present and non-empty. */
  hasData: boolean;
  /** Rendered when hasData. */
  children?: React.ReactNode;
  report?: SiteRagReport;
  sectionId?: string;
  required?: boolean;
  /** Flat matches Feasibility inline sections; card preserves legacy Sleek card chrome. */
  layout?: 'flat' | 'card';
  /** When hasData is false, overrides the default insufficient-data message. */
  emptyMessage?: string;
  /** When true, omit the H2 (parent ClassicSectionShell owns the heading). */
  suppressHeading?: boolean;
  /**
   * Permit the measured paginator to fold this complete, singleton section
   * forward onto the following page when a transactional fit check proves the
   * combined page clears the production blank-tail ceiling.
   */
  pagedForwardFold?: boolean;
}

export const SectionShell: React.FC<SectionShellProps> = ({
  label,
  hasData,
  children,
  required,
  report,
  sectionId: sectionIdProp,
  layout = 'flat',
  emptyMessage: emptyMessageProp,
  suppressHeading: suppressHeadingProp,
  pagedForwardFold = false,
}) => {
  const theme = useEffectiveReportTheme();
  const parentCtx = useSectionShellContext();
  const sectionIdFromContext = React.useContext(SectionIdContext);
  const routerChrome = React.useContext(RouterSectionChromeContext);
  const sectionId = sectionIdProp ?? sectionIdFromContext;
  const showSection = required ?? (sectionId != null);
  if (!hasData && !showSection) return null;

  const padClass = theme?.density === 'compact' ? 'p-4' : 'p-6';
  const anchorOwnedByRouter = sectionIdFromContext != null && sectionId === sectionIdFromContext;
  const suppressHeading = suppressHeadingProp ?? anchorOwnedByRouter;
  const idProps = sectionId && !anchorOwnedByRouter
    ? { id: sectionId, 'data-section': sectionId, 'data-section-id': sectionId, 'data-sleek-section': '' as const }
    : {};

  const shellClass = layout === 'card'
    ? `rounded-xl shadow-sm border ${padClass} flex flex-col break-inside-avoid overflow-hidden`
    : 'space-y-4';
  // Theme-aware hairline for the card shell (replaces the fixed
  // border-border-subtle app token so the shell follows the report preset).
  // FLATTEN-TO-CREAM — the card surface inherits the cream page tint via
  // `--page-bg` instead of a hardcoded `bg-white`, so a card-layout shell never
  // renders a white surface on cream paper. (The `card` layout is currently
  // unused — every report section is `flat` — but this keeps it cream-safe.)
  const shellStyle: React.CSSProperties | undefined =
    layout === 'card'
      ? { borderColor: roleColor(theme, 'hair'), background: 'var(--page-bg, transparent)' }
      : undefined;

  const headingFallback = routerChrome.sectionDefaultHeading?.(sectionId ?? '', label) ?? label;
  const emptyMessage = resolveSectionEmptyMessage(report, sectionId, emptyMessageProp);
  const headingNode = sectionId && routerChrome.onHeadingChange ? (
    <EditableHeading
      headingKey={sectionId}
      defaultText={headingFallback}
      className={REPORT_SECTION_HEADING_TEXT_CLASS}
      isEditing={routerChrome.isEditing ?? false}
      overrides={routerChrome.headingOverrides ?? {}}
      onChange={routerChrome.onHeadingChange}
    />
  ) : label;

  const Wrapper = suppressHeading ? 'div' : 'section';

  // P1 section-manifest accounting — when a section is SHOWN but carries no
  // data, it renders the empty-state placeholder below (an intentional, reasoned
  // omission, NOT a silent drop). Stamp a minimal machine-readable marker so the
  // conformance gate classifies this as `gracefully-omitted` rather than DROPPED.
  // Renderer-side only: no behaviour change, just the marker the probe reads.
  const omitProps = !hasData ? { 'data-omit-reason': 'insufficient-data' } : {};

  return (
    <Wrapper
      {...idProps}
      {...omitProps}
      className={shellClass}
      style={shellStyle}
      data-section-layout={layout}
      data-paged-forward-fold={pagedForwardFold ? 'true' : undefined}
    >
      <SectionShellContext.Provider value={{ report, sectionId, isEditorialActive: parentCtx.isEditorialActive }}>
        {!suppressHeading && (
          <h2
            data-heading="true"
            className={`${REPORT_SECTION_H2_CLASS} ${layout === 'flat' ? 'mb-0' : 'mb-4'}`}
            style={reportSectionHeadingStyleScaled(theme)}
          >
            {headingNode}
          </h2>
        )}

      {report && sectionId && !anchorOwnedByRouter && (
        <ProvenanceStrip report={report} sectionId={sectionId} />
      )}

      {report && sectionId && !suppressHeading && routerChrome.isAdmin && routerChrome.onSectionRegen && (
        <MetaLeakChip
          report={report}
          sectionId={sectionId}
          sectionLabel={label}
          isAdmin={routerChrome.isAdmin}
          onRegenerate={routerChrome.onSectionRegen}
        />
      )}

      {hasData ? (
        // W0 shared-foundation fix — this wrapper previously applied
        // REPORT_BODY_TEXT_CLASS, whose `text-text-secondary` is an APP-chrome
        // token: every bare-prose modern section inherited app colours instead
        // of the active report preset. Keep the size/leading intent
        // (text-sm leading-relaxed) and source the prose ink + face from the
        // theme — roleColor(theme,'body') + var(--report-body-font) — matching
        // the v1.4.0 feasibility editorial sections. Children that paint their
        // own roleColor() ink simply override the inherited value.
        <div
          className="space-y-4 text-sm leading-relaxed"
          style={{
            color: roleColor(theme, 'body'),
            fontFamily: 'var(--report-body-font, inherit)',
          }}
        >
          {children}
        </div>
      ) : (
        <div
          className="text-xs italic py-2 whitespace-pre-wrap"
          // William QA 2026-06-28 — left accent rule removed report-wide; the
          // insufficient-data note reads as a flush-left italic muted note on
          // the cream page.
          style={{
            color: roleColor(theme, 'muted'),
            background: 'transparent',
          }}
        >
          {emptyMessage}
        </div>
      )}
      </SectionShellContext.Provider>
    </Wrapper>
  );
};
